import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { Table } from "reactstrap";
import {
  FacebookAnalyticsSnapshot,
  FacebookAudienceSegment,
  FacebookPostMetric,
  FacebookStatCard,
  FacebookTrendPoint,
} from "./facebookAnalyticsMock";
import "./FacebookMetricsDashboard.scss";

interface Props {
  snapshot: FacebookAnalyticsSnapshot;
  publicationsLoading?: boolean;
}

const formatNumber = (value: number | string, unit?: "percent") => {
  if (typeof value === "string") return value;
  const formatted = new Intl.NumberFormat("es-CL", {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
  return unit === "percent" ? `${formatted}%` : formatted;
};

const metricIcon = (id: string): IconProp => {
  const map: Record<string, IconProp> = {
    followers: ["fas", "users"],
    views: ["fas", "eye"],
    pageViews: ["fas", "flag"],
    content: ["fas", "newspaper"],
    dailyNewFollowers: ["fas", "user-plus"],
    pageViewsPerDay: ["fas", "calendar-day"],
    postsPerDay: ["fas", "calendar-alt"],
    postsPerWeek: ["fas", "calendar-week"],
    balance: ["fas", "balance-scale"],
    acquired: ["fas", "arrow-up"],
    lost: ["fas", "arrow-down"],
    reactions: ["fas", "thumbs-up"],
    totalClicks: ["fas", "mouse-pointer"],
    engagement: ["fas", "chart-line"],
    interactions: ["fas", "comments"],
    avgReach: ["fas", "bullseye"],
    posts: ["fas", "images"],
    comments: ["fas", "comment"],
    shares: ["fas", "share"],
    clicks: ["fas", "mouse-pointer"],
  };
  return map[id] ?? ["fas", "chart-bar"];
};

const deltaIcon = (direction?: FacebookStatCard["direction"]): IconProp => {
  if (direction === "down") return ["fas", "arrow-down"];
  if (direction === "flat") return ["fas", "minus"];
  return ["fas", "arrow-up"];
};

const statTone = (direction?: FacebookStatCard["direction"]) => {
  if (direction === "up") return "up";
  if (direction === "down") return "down";
  return "flat";
};

const sectionTabs = [
  { id: "overview", label: "Visión general" },
  { id: "demographics", label: "Demografía" },
  { id: "clicks", label: "Clics en la página" },
  { id: "posts", label: "Publicaciones en el periodo" },
  // TODO: Competidores — pestaña deshabilitada hasta implementar comparativa de páginas
  // { id: "competitors", label: "Competidores" },
] as const;

type SectionId = (typeof sectionTabs)[number]["id"];

const StatCard = ({ item }: { item: FacebookStatCard }) => {
  const tone = statTone(item.direction);
  return (
    <div className="facebook-analytics__stat-card">
      <div className="facebook-analytics__stat-head">
        <span className="facebook-analytics__stat-icon">
          <FontAwesomeIcon icon={metricIcon(item.id)} fixedWidth />
        </span>
        {item.direction ? (
          <span className={`facebook-analytics__delta facebook-analytics__delta--${tone}`}>
            <FontAwesomeIcon icon={deltaIcon(item.direction)} />
          </span>
        ) : null}
      </div>
      <strong className="facebook-analytics__stat-value">
        {formatNumber(item.value, item.unit)}
      </strong>
      <span className="facebook-analytics__stat-title">{item.title}</span>
      {item.description ? (
        <span className="facebook-analytics__stat-description">{item.description}</span>
      ) : null}
    </div>
  );
};

const SegmentList = ({
  title,
  items,
}: {
  title: string;
  items: FacebookAudienceSegment[];
}) => {
  const max = Math.max(
    ...items.map((item) => (typeof item.value === "number" ? item.value : 0)),
    1
  );
  return (
    <div className="facebook-analytics__panel">
      <h3 className="facebook-analytics__panel-title">{title}</h3>
      <div className="facebook-analytics__segments">
        {items.map((item) => {
          const isNumber = typeof item.value === "number";
          const segmentValue = isNumber ? Number(item.value) : 0;
          return (
            <div className="facebook-analytics__segment" key={item.label}>
              <div className="facebook-analytics__segment-row">
                <span>{item.label}</span>
                <strong>
                  {isNumber
                    ? `${formatNumber(item.value)}${item.detail ?? ""}`
                    : item.value}
                </strong>
              </div>
              {isNumber ? (
                <div className="facebook-analytics__segment-track">
                  <span style={{ width: `${Math.max(4, (segmentValue / max) * 100)}%` }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TrendChart = ({
  title,
  points,
  lineKey,
  barKey,
  lineLabel,
  barLabel,
}: {
  title: string;
  points: FacebookTrendPoint[];
  lineKey: keyof FacebookTrendPoint;
  barKey?: keyof FacebookTrendPoint;
  lineLabel: string;
  barLabel?: string;
}) => {
  const numeric = (key: keyof FacebookTrendPoint) =>
    points.map((point) => Number(point[key] ?? 0));
  const line = numeric(lineKey);
  const bars = barKey ? numeric(barKey) : [];
  const yMax = Math.max(...line, ...bars, 1);
  const linePoints = line
    .map((value, index) => {
      const x = (index / Math.max(points.length - 1, 1)) * 100;
      const y = ((yMax - value) / yMax) * 84 + 8;
      return `${x},${y}`;
    })
    .join(" ");
  const barMax = Math.max(...bars, 1);

  return (
    <div className="facebook-analytics__panel facebook-analytics__panel--chart">
      <div className="facebook-analytics__chart-head">
        <h3 className="facebook-analytics__panel-title">{title}</h3>
        <div className="facebook-analytics__legend">
          <span className="facebook-analytics__legend-line">{lineLabel}</span>
          {barLabel ? <span className="facebook-analytics__legend-bar">{barLabel}</span> : null}
        </div>
      </div>
      <div className="facebook-analytics__chart">
        <div className="facebook-analytics__bars" style={{ "--facebook-days": points.length } as React.CSSProperties}>
          {bars.map((value, index) => (
            <span
              key={`${points[index]?.label}-${index}`}
              style={{ height: `${Math.min(100, (value / barMax) * 72)}%` }}
            />
          ))}
        </div>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          <polyline
            fill="none"
            stroke="#1877f2"
            strokeWidth={1.25}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            points={linePoints}
          />
        </svg>
      </div>
      <div className="facebook-analytics__chart-labels" style={{ "--facebook-days": points.length } as React.CSSProperties}>
        {points.map((point) => (
          <span key={point.label}>{point.label}</span>
        ))}
      </div>
    </div>
  );
};

const NoticeCard = ({ items }: { items: string[] }) => (
  <div className="facebook-analytics__notice">
    <div className="facebook-analytics__notice-icon">
      <FontAwesomeIcon icon={["fas", "info"]} />
    </div>
    <div>
      <strong>Actualización de métricas Meta</strong>
      {items.map((item) => (
        <p key={item}>{item}</p>
      ))}
    </div>
  </div>
);

const PostTable = ({ posts }: { posts: FacebookPostMetric[] }) => {
  const csvData = posts.map((post) => ({
    post: post.caption,
    tipo: post.typeLabel,
    fecha: `${post.dateLabel} ${post.timeLabel}`,
    alcance: post.reach,
    visualizaciones: post.views,
    reacciones: post.reactions,
    comentarios: post.comments,
    compartidos: post.shares,
    clics: post.clicks,
  }));

  return (
    <div className="facebook-analytics__panel facebook-analytics__posts-panel">
      <div className="facebook-analytics__table-toolbar">
        <div>
          <h3 className="facebook-analytics__panel-title">Lista de publicaciones</h3>
          <span>Buscar · Descargar CSV · Columnas · Añadir a un dashboard</span>
        </div>
        <CSVLink
          data={csvData}
          filename={`facebook-posts-${Date.now()}.csv`}
          className="facebook-analytics__csv-link"
        >
          <FontAwesomeIcon icon={["fas", "download"]} />
          Descargar CSV
        </CSVLink>
      </div>
      <div className="table-responsive">
        <Table hover className="facebook-analytics__table mb-0" size="sm">
          <thead>
            <tr>
              <th>Posts</th>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Alcance</th>
              <th>Visualizaciones</th>
              <th>Reacciones</th>
              <th>Comentarios</th>
              <th>Compartidos</th>
              <th>Clics</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id}>
                <td>
                  <div className="facebook-analytics__post-cell">
                    <span className="facebook-analytics__post-thumb">
                      <FontAwesomeIcon icon={["fas", "image"]} />
                    </span>
                    <span>
                      {post.status ? (
                        <em className="facebook-analytics__new-badge">{post.status}</em>
                      ) : null}
                      {post.caption}
                    </span>
                  </div>
                </td>
                <td>{post.typeLabel}</td>
                <td>
                  <strong>{post.dateLabel}</strong>
                  <small>{post.timeLabel}</small>
                </td>
                <td>{formatNumber(post.reach)}</td>
                <td>{formatNumber(post.views)}</td>
                <td>{formatNumber(post.reactions)}</td>
                <td>{formatNumber(post.comments)}</td>
                <td>{formatNumber(post.shares)}</td>
                <td>{formatNumber(post.clicks)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="facebook-analytics__pagination-note">
        Elementos por página: 5 · 1-5 de 5
      </div>
    </div>
  );
};

export const FacebookMetricsDashboard = ({
  snapshot,
  publicationsLoading = false,
}: Props) => {
  const [activeSection, setActiveSection] = useState<SectionId>("overview");

  const activeMetricsCount = useMemo(
    () =>
      snapshot.growth.summary.length +
      snapshot.growth.cadence.length +
      snapshot.followerBalance.length +
      snapshot.viewedPosts.length +
      snapshot.clicks.summary.length +
      snapshot.publications.summary.length +
      snapshot.publications.interactions.length +
      snapshot.publications.rates.length,
    [snapshot]
  );

  return (
    <div className="facebook-analytics">
      <div className="facebook-analytics__tabs">
        {sectionTabs.map((tab) => (
          <button
            type="button"
            key={tab.id}
            className={cn("facebook-analytics__tab", {
              "facebook-analytics__tab--active": activeSection === tab.id,
            })}
            onClick={() => setActiveSection(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSection === "overview" ? (
        <>
          <NoticeCard items={snapshot.metaNotice} />
          <section className="facebook-analytics__section">
            <div className="facebook-analytics__section-head">
              <div>
                <span>Visión general</span>
                <h2>Crecimiento</h2>
              </div>
              <strong>{activeMetricsCount} métricas del PDF</strong>
            </div>
            <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--four">
              {snapshot.growth.summary.map((item) => (
                <StatCard key={item.id} item={item} />
              ))}
            </div>
            <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--four">
              {snapshot.growth.cadence.map((item) => (
                <StatCard key={item.id} item={item} />
              ))}
            </div>
            <TrendChart
              title="Evolución del periodo"
              points={snapshot.growth.trend}
              lineKey="followers"
              barKey="views"
              lineLabel="Seguidores"
              barLabel="Visualizaciones"
            />
          </section>
          <section className="facebook-analytics__section facebook-analytics__section-grid">
            <div>
              <div className="facebook-analytics__section-head facebook-analytics__section-head--compact">
                <h2>Balance de Seguidores</h2>
              </div>
              <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--two">
                {snapshot.followerBalance.map((item) => (
                  <StatCard key={item.id} item={item} />
                ))}
              </div>
            </div>
            <div>
              <div className="facebook-analytics__section-head facebook-analytics__section-head--compact">
                <h2>Publicaciones vistas en el periodo</h2>
              </div>
              <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--two">
                {snapshot.viewedPosts.map((item) => (
                  <StatCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </section>
        </>
      ) : null}

      {activeSection === "demographics" ? (
        <section className="facebook-analytics__section facebook-analytics__section-grid">
          <SegmentList title="Seguidores por país" items={snapshot.demographics.countries} />
          <SegmentList title="Seguidores por ciudad" items={snapshot.demographics.cities} />
        </section>
      ) : null}

      {activeSection === "clicks" ? (
        <section className="facebook-analytics__section">
          <div className="facebook-analytics__section-head">
            <div>
              <span>Clics en la página</span>
              <h2>Clics</h2>
            </div>
          </div>
          <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--three">
            {snapshot.clicks.summary.map((item) => (
              <StatCard key={item.id} item={item} />
            ))}
          </div>
          <SegmentList title="Tipos · Visualizaciones" items={snapshot.clicks.types} />
        </section>
      ) : null}

      {activeSection === "posts" ? (
        <section className="facebook-analytics__section">
          {publicationsLoading ? (
            <p className="facebook-analytics__loading-hint text-muted mb-3">
              Cargando publicaciones del periodo…
            </p>
          ) : null}
          <div className="facebook-analytics__section-head">
            <div>
              <span>Publicaciones en el periodo</span>
              <h2>Resumen</h2>
            </div>
          </div>
          <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--five">
            {snapshot.publications.summary.map((item) => (
              <StatCard key={item.id} item={item} />
            ))}
          </div>
          <TrendChart
            title="Visualizaciones e interacciones"
            points={snapshot.publications.chartTrend}
            lineKey="views"
            barKey="reactions"
            lineLabel="Visualizaciones"
            barLabel="Reacciones"
          />
          <div className="facebook-analytics__section-head facebook-analytics__section-head--compact">
            <h2>Interacciones</h2>
          </div>
          <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--five">
            {snapshot.publications.interactions.map((item) => (
              <StatCard key={item.id} item={item} />
            ))}
          </div>
          <div className="facebook-analytics__stat-grid facebook-analytics__stat-grid--three">
            {snapshot.publications.rates.map((item) => (
              <StatCard key={item.id} item={item} />
            ))}
          </div>
          <SegmentList title="Orgánicas / De Pago" items={snapshot.publications.organicPaid} />
          <PostTable posts={snapshot.publications.posts} />
        </section>
      ) : null}

      {/* TODO: Competidores — reactivar cuando exista API / diseño de producto
      {activeSection === "competitors" ? (
        <section className="facebook-analytics__section">
          <div className="facebook-analytics__competitor-toolbar">
            {snapshot.competitors.filters.map((filter) => (
              <span key={filter}>{filter}</span>
            ))}
          </div>
          <div className="facebook-analytics__empty">
            <FontAwesomeIcon icon={["fas", "users"]} />
            <h2>{snapshot.competitors.title}</h2>
            <strong>{snapshot.competitors.message}</strong>
            <p>{snapshot.competitors.hint}</p>
          </div>
        </section>
      ) : null}
      */}
    </div>
  );
};
